'use client';
import { useEffect, useState } from 'react';
import Link from 'components/AppSafeLink/AppSafeLink';
import { useSearchParams } from 'next/navigation';
import '/public/css/pages/newsletterPage.css';

import ContentGrid from '../../grids/ContentGrid';
import Sidebar from '../../components/Sidebar/Sidebar';
import useEmailFormSubmit from '../../hooks/useEmailSubscribe';
import { isValidAddress } from '../../utils/emailValidation';
import styles from './page.module.css';
import ErrorIcon from '../../components/Icons/ErrorIcon';
import SuccessIcon from '../../components/Icons/SuccessIcon';
import LoadingSpinnerIcon from '../../components/Icons/LoadingSpinnerIcon';
import SubscribeIcon from '../../components/Icons/SubscribeIcon';

export default function Newsletter() {
  const searchParams = useSearchParams();
  const [params, setParams] = useState<Record<string, string> | undefined>();
  const [newsletterIds, setNewsletterIds] = useState<number[]>([]);
  const [email, setEmail] = useState('');
  const [postalcode, setPostalcode] = useState('');
  const [isClicked, setIsClicked] = useState(false);
  const pageTopic = '';

  const { processMessage, processSubscribe, hasError, isSuccessful } =
    useEmailFormSubmit();

  useEffect(() => {
    if (searchParams) {
      const params = Object.fromEntries(searchParams.entries());
      setParams(params);
    }
  }, [searchParams]);

  function processCheckbox(e: React.MouseEvent<HTMLInputElement>) {
    if (
      e.currentTarget.checked &&
      newsletterIds.indexOf(Number(e.currentTarget.value)) == -1
    ) {
      setNewsletterIds([...newsletterIds, Number(e.currentTarget.value)]);
      setIsClicked(false);
    } else {
      setNewsletterIds(
        newsletterIds.filter((itm) => itm != Number(e.currentTarget.value))
      );
    }
  }

  const submitDisabled =
    isClicked || newsletterIds.length === 0 || !isValidAddress(email);

  return (
    <ContentGrid sidebar={<Sidebar pageTopic={pageTopic} newsletter={false} />}>
      <div className={styles.wrapper}>
        <h1 className={styles.title}>Subscribe to Email Newsletters</h1>

        <div className={styles.formWrapper}>
          <>
            <p className={styles.blurb}>
              Stay informed with our curated newsletters. Select your interests
              below and get the latest news and insights delivered straight to
              your inbox.
            </p>

            <form>
              <div className={styles.formEmail}>
                <div className={styles.inputGroup}>
                  <label htmlFor="email">
                    Email Address <b title="required">*</b>
                  </label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    required={true}
                    maxLength={254}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setIsClicked(false);
                    }}
                    placeholder="Enter your email address"
                  />
                </div>

                <div className={styles.inputGroup}>
                  <label htmlFor="postalcode">ZIP Code</label>
                  <input
                    type="text"
                    name="postalcode"
                    id="postalcode"
                    required={false}
                    maxLength={10}
                    onChange={(e) => {
                      setPostalcode(e.target.value);
                      setIsClicked(false);
                    }}
                    placeholder="Enter your zip code"
                  />
                </div>
              </div>
              <h2 className={styles.sectionTitle}>
                Choose the newsletters you wish to receive:
              </h2>
              <div className={styles.newsletterSection}>
                <div className={styles.formNewsletter}>
                  <div className={styles.checkboxContainer}>
                    <input
                      type="checkbox"
                      name="subscription_request[publication_lists][3904]"
                      id="mntodayweekend"
                      value={3904}
                      onClick={processCheckbox}
                    />
                  </div>
                  <label
                    className={styles.newsletterDescription}
                    htmlFor="mntodayweekend"
                  >
                    <div className={styles.newsletterContentContainer}>
                      <img
                        className={styles.newsletterImage}
                        src="/img/newsletter-img/mntodayweekend.png"
                        alt="Minnesota Today Weekend Edition"
                      />
                      <div className={styles.newsletterDescriptionInner}>
                        <p>
                          Get the weekend weather forecast, events happening
                          around the state and can&apos;t-miss Minnesota stories
                          from the past week delivered to your inbox every
                          Saturday morning.
                        </p>
                        <Link
                          href="https://cloud.connect.mpr.org/mn-today-weekend"
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.previewLink}
                        >
                          See the latest edition
                        </Link>
                      </div>
                    </div>
                  </label>
                </div>

                <div className={styles.formNewsletter}>
                  <div className={styles.checkboxContainer}>
                    <input
                      type="checkbox"
                      name="subscription_request[publication_lists][529]"
                      id="minnesota-today"
                      value={529}
                      onClick={processCheckbox}
                    />
                  </div>
                  <label
                    className={styles.newsletterDescription}
                    htmlFor="minnesota-today"
                  >
                    <div className={styles.newsletterContentContainer}>
                      <img
                        className={styles.newsletterImage}
                        src="/img/newsletter-img/mntoday.png"
                        alt="Minnesota Today Newsletter"
                      />
                      <div className={styles.newsletterDescriptionInner}>
                        <p>
                          The top stories and regional headlines. Delivered
                          weekday mornings.
                        </p>
                        <Link
                          href="https://cloud.connect.mpr.org/mprnewsam_update"
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.previewLink}
                        >
                          See the latest edition
                        </Link>
                      </div>
                    </div>
                  </label>
                </div>

                <div className={styles.formNewsletter}>
                  <div className={styles.checkboxContainer}>
                    <input
                      type="checkbox"
                      name="subscription_request[publication_lists][459]"
                      id="the-thread"
                      value={459}
                      onClick={processCheckbox}
                    />
                  </div>
                  <label
                    className={styles.newsletterDescription}
                    htmlFor="the-thread"
                  >
                    <div className={styles.newsletterContentContainer}>
                      <img
                        className={styles.newsletterImage}
                        src="/img/newsletter-img/thread.png"
                        alt="The Thread Newsletter"
                      />
                      <div className={styles.newsletterDescriptionInner}>
                        <p>
                          Kerri Miller&lsquo;s exclusive weekly book pick, along
                          with literary news and event details. Delivered
                          Fridays.
                        </p>
                        <Link
                          href="https://cloud.connect.mpr.org/thethread"
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.previewLink}
                        >
                          See the latest edition
                        </Link>
                      </div>
                    </div>
                  </label>
                </div>

                <div className={styles.formNewsletter}>
                  <div className={styles.checkboxContainer}>
                    <input
                      type="checkbox"
                      name="subscription_request[publication_lists][528]"
                      id="capitol-view"
                      value={528}
                      onClick={processCheckbox}
                    />
                  </div>
                  <label
                    className={styles.newsletterDescription}
                    htmlFor="capitol-view"
                  >
                    <div className={styles.newsletterContentContainer}>
                      <img
                        className={styles.newsletterImage}
                        src="/img/newsletter-img/capitolview.png"
                        alt="Capitol View Newsletter"
                      />
                      <div className={styles.newsletterDescriptionInner}>
                        <p>
                          Each day, our politics team gives you links to
                          must-read articles from around Minnesota.
                        </p>
                        <Link
                          href="https://cloud.connect.mpr.org/capitolview"
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.previewLink}
                        >
                          See the latest edition
                        </Link>
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              <div className={styles['submit-container']}>
                <p>
                  By submitting, you consent to receive information about our
                  programs and offerings. The personally identifying information
                  you provide will not be sold, shared, or used for purposes
                  other than to communicate with you about MPR News. You may
                  opt-out at any time clicking the unsubscribe link at the
                  bottom of any email communication.
                </p>
                {processMessage !== '' && (
                  <div className={hasError ? styles.error : styles.noerror}>
                    {hasError && <ErrorIcon />}
                    {!hasError && <SuccessIcon />}
                    {processMessage}
                  </div>
                )}
                {!isSuccessful && (
                  <button
                    className={styles.subscribeBtn}
                    type="submit"
                    disabled={submitDisabled}
                    onClick={async (e) => {
                      e.preventDefault();
                      setIsClicked(true);
                      await processSubscribe(
                        email,
                        postalcode,
                        newsletterIds,
                        params
                      );
                    }}
                  >
                    {isClicked && !hasError ? (
                      <>
                        <LoadingSpinnerIcon />
                        Processing...
                      </>
                    ) : (
                      <>
                        <SubscribeIcon />
                        Subscribe Now
                      </>
                    )}
                  </button>
                )}
              </div>
            </form>
          </>
        </div>
      </div>
    </ContentGrid>
  );
}
